// PackageEditDialog.jsx
import React, { useEffect, useRef, useState } from "react";
import {
    Dialog,
    IconButton,
    Box,
    Avatar,
    Autocomplete,
    TextField,
    MenuItem,
    Button,
    Slide,
} from "@mui/material";
import { GetAgentList } from "api/agent";
import { CloseOutlined } from "@mui/icons-material";
import {
    playAlertSound,
    playIncorrectSound,
} from "utils";
import { GetCustomersList } from "api/customers";
import { GetRouteList } from "api/routes";
import { isMobile } from "react-device-detect";
import { PatchPackageFields, PatchPackageScanAssign } from "api/packages";
import { SubmitPackageEditRequest } from "api/packageChangeRequest";
import { useTranslation } from "react-i18next";
import { user } from "localStorage";
import Swal from "sweetalert2";
import { sectionLabelSx, fieldSx, accentFieldSx, ToggleCard } from "components/bento/BentoFormControls";
import {
    BG_WHITE,
    BG_TINT,
    BORDER,
    DIVIDER,
    RADIUS_CARD,
    PURPLE_DARK,
    TEXT_DARK,
    TEXT_BODY,
    TEXT_SUBTLE,
} from "components/bento/bentoTokens";

// Transition component for dialog animations
const Transition = React.forwardRef(function Transition(props, ref) {
    return <Slide direction="up" ref={ref} {...props} />;
});

const initials = (name) => {
    const parts = (name || "").trim().split(/\s+/).filter(Boolean);
    if (parts.length === 0) return "?";
    return parts.slice(0, 2).map((p) => p[0]).join("").toUpperCase();
};

// Define the props for the PackageEditDialog
const PackageEditDialogForScan = ({
    open,
    onClose,
    packageData,
    onSuccess,
    selectedStation
    // Data and state handlers:
}) => {
    // Package data
    const [packageId, setPackageId] = useState("");
    const [tracking, setTracking] = useState("");
    const [routeId, setRouteId] = useState("");
    const [customer, setCustomer] = useState(null);
    const [remark, setRemark] = useState("");
    const [isDeliveryRequired, setIsDeliveryRequired] = useState(false);
    const [isCOD, setIsCOD] = useState(false);
    const [isSubCustomer, setIsSubCustomer] = useState(false);
    const [level, setLevel] = useState("1");
    const [agentList, setAgentList] = useState([]);
    const [agentId, setAgentId] = useState("");
    const [isForwardRequired, setIsForwardRequired] = useState(false);
    const [isSelfOrder, setIsSelfOrder] = useState(false);
    const [deliveryAddress, setDeliveryAddress] = useState(null);
    const [forwardAddress, setForwardAddress] = useState(null);
    const [deliveryAddresses, setDeliveryAddresses] = useState([]);
    const [forwardAddresses, setForwardAddresses] = useState([]);

    // Front-end data
    const [searchCustomer, setSearchCustomer] = useState("");
    const [customerList, setCustomerList] = useState([]);
    const searchCustomerTimeout = useRef(null); // Ref to hold the timeout ID
    const [routeList, setRouteList] = useState([]);
    const [isNewCustomerSelected, setIsNewCustomerSelected] = useState(false);
    const hasInitializedCustomerRef = useRef(false); // one-time sync guard, reset per package load

    // Snapshot of the original values for the fields this dialog actually manages —
    // used on submit to send only what changed via the lightweight patch-fields endpoint,
    // instead of clobbering unrelated fields (description/amount/quantity/etc.) with the
    // hardcoded placeholder values this dialog used to send for fields it doesn't show.
    const originalRef = useRef({});

    const _user = user();
    const isAgentUser = _user?.role === "agent";
    // Only admin can save an edit directly — every other role's save goes through
    // /api/package-change-request instead and waits for admin approval.
    const canEditDirectly = _user?.role === "admin";
    const { t } = useTranslation();

    // Clears all locally-managed form state. Needed because the dialog is
    // rendered with keepMounted, so its state otherwise survives across
    // opens and briefly shows the previous package's submitted values.
    const resetForm = () => {
        setPackageId("");
        setTracking("");
        setRouteId("");
        setCustomer(null);
        setRemark("");
        setIsDeliveryRequired(false);
        setIsCOD(false);
        setIsSubCustomer(false);
        setLevel("1");
        setAgentId("");
        setIsForwardRequired(false);
        setIsSelfOrder(false);
        setDeliveryAddress(null);
        setForwardAddress(null);
        setSearchCustomer("");
        setCustomerList([]);
        setIsNewCustomerSelected(false);
        hasInitializedCustomerRef.current = false;
        originalRef.current = {};
    };

    // Reset once the dialog closes (Cancel, backdrop click, or after a
    // successful submit), instead of leaving stale values for the next open.
    useEffect(() => {
        if (!open) resetForm();
    }, [open]);

    // Parcel still has no customer (or no route, with the customer left as-is) —
    // staff are filling in missing data at scan time, not correcting existing data,
    // so it saves directly with no admin approval. Mirrors backend scanAssign's rule.
    const isScanAssignNow = (customerId = customer ? customer._id : "") =>
        !originalRef.current.customerId ||
        (!originalRef.current.routeId && customerId === originalRef.current.customerId);

    // When opened from a scan, only show routes that pass through the scanned station
    // (selectedStation), so the user can't assign a route the parcel isn't on. The
    // selected route always stays in the list — a customer's default route may not pass
    // through this station, and the field must not go blank. Same as mobile PackageForm.
    const routeOptions = (() => {
        const all = routeList || [];
        const visible = all.filter(
            (r) =>
                !selectedStation ||
                (r.navigator || []).some(
                    (n) =>
                        n.station === selectedStation.id ||
                        n.nextStation === selectedStation.id
                )
        );
        if (routeId && routeId !== "N/A" && !visible.some((r) => r.id === routeId)) {
            visible.push(all.find((r) => r.id === routeId) || { id: routeId, name: "" });
        }
        return visible;
    })();

    const handleFormSubmited = async (e) => {
        e.preventDefault();
        if (isSubCustomer && !agentId) {
            Swal.fire({
                title: t("scan.errorTitle"),
                icon: "error",
                text: t("scan.agentRequired"),
                showConfirmButton: true,
            });
            return;
        }
        const customerId = customer ? customer._id : "";

        // Only send fields that actually changed — this dialog never shows
        // description/orderId/amount/quantity/shippingFee/paymentStatus/whatsappStatus/
        // finalShippingFee/agentWhatsappStatus, so they must never be sent at all.
        const normalize = (v) => {
            if (v === null || v === undefined) return "";
            if (typeof v === "object") return JSON.stringify(v);
            return String(v);
        };
        const fieldsToUpdate = {};
        const setIfChanged = (key, currentVal) => {
            if (normalize(currentVal) === normalize(originalRef.current[key])) return;
            fieldsToUpdate[key] = currentVal;
        };

        setIfChanged("customerId", customerId);
        setIfChanged("routeId", routeId);
        setIfChanged("remark", remark);
        setIfChanged("isCOD", isCOD);
        setIfChanged("isSelfOrder", isSelfOrder);
        setIfChanged("isSubCustomer", isSubCustomer);
        setIfChanged("agentId", agentId);
        setIfChanged("level", level);
        setIfChanged("isDeliveryRequired", isDeliveryRequired);
        setIfChanged("isForwardRequired", isForwardRequired);
        setIfChanged("deliveryAddress", deliveryAddress);
        setIfChanged("forwardAddress", forwardAddress);

        if (Object.keys(fieldsToUpdate).length === 0) {
            onSuccess();
            Swal.fire({
                title: t("scan.updatedTitle"),
                icon: "success",
                timer: 1500,
            });
            return;
        }

        const isScanAssign = isScanAssignNow(customerId);

        if (!canEditDirectly && isScanAssign) {
            const res = await PatchPackageScanAssign(packageId, fieldsToUpdate);
            if (res && res.success) {
                onSuccess();
                Swal.fire({
                    title: t("scan.updatedTitle"),
                    icon: "success",
                    text: res.message,
                    timer: 1500,
                });
            } else {
                playIncorrectSound();
                Swal.fire({
                    title: t("scan.errorTitle"),
                    icon: "error",
                    text: res?.message,
                });
            }
            return;
        }

        if (!canEditDirectly) {
            // Manager (or any role without direct edit access) — submit for
            // admin approval instead of saving immediately.
            const { value: reason } = await Swal.fire({
                title: t("packageChangeRequest.reasonPromptTitle"),
                input: "textarea",
                inputPlaceholder: t("packageChangeRequest.reasonPlaceholder"),
                showCancelButton: true,
                confirmButtonText: t("packageChangeRequest.submitButton"),
                cancelButtonText: t("common.cancel"),
                inputValidator: (value) => (!value || !value.trim() ? t("packageChangeRequest.reasonRequired") : undefined),
            });
            if (!reason) return;

            const res = await SubmitPackageEditRequest(packageId, fieldsToUpdate, reason);
            if (res.success) {
                onSuccess();
                Swal.fire({
                    title: t("packageChangeRequest.submittedTitle"),
                    icon: "success",
                    text: t("packageChangeRequest.submittedText"),
                    timer: 2000,
                });
            } else {
                playAlertSound();
                Swal.fire({ title: t("packageChangeRequest.errorTitle"), icon: "error", text: res.message });
            }
            return;
        }

        PatchPackageFields(packageId, fieldsToUpdate).then((res) => {
            if (res.status === 200) {
                onSuccess();
                // clearAllState();
                // playCorrectSound();
                Swal.fire({
                    title: t("scan.updatedTitle"),
                    icon: "success",
                    text: res.message,
                    timer: 1500,
                });
            } else if (res.status === 400) {
                playAlertSound();
                Swal.fire({
                    title: t("scan.errorTitle"),
                    icon: "error",
                    text: `Status ${res.status} - ${res.message}`,
                });
            } else {
                playIncorrectSound();
                Swal.fire({
                    title: t("scan.errorTitle"),
                    icon: "error",
                    text: res.message,
                });
            }
        });
    };

    // Set defaults when a new customer is selected
    useEffect(() => {
        // Set delivery & forward addresses from customer
        if (customer) {
            if ("forwardAddresses" in customer)
                setForwardAddresses(customer.forwardAddresses);
            if ("deliveryAddresses" in customer)
                setDeliveryAddresses(customer.deliveryAddresses);
        }

        if (isNewCustomerSelected && customer) {
            if ("default_route" in customer) setRouteId(customer.default_route);
            if ("isCOD" in customer) setIsCOD(customer.isCOD);
            if ("isDeliveryRequired" in customer)
                setIsDeliveryRequired(customer.isDeliveryRequired);
            if ("isForwardRequired" in customer)
                setIsForwardRequired(customer.isForwardRequired);
            if ("isSubCustomer" in customer) setIsSubCustomer(customer.isSubCustomer);
            if ("isSelfOrder" in customer) setIsSelfOrder(customer.isSelfOrder);
            if ("agentId" in customer) setAgentId(customer.agentId);
            if ("level" in customer) setLevel(customer.level);

            setIsNewCustomerSelected(false);
        }
    }, [isNewCustomerSelected, customer]);

    useEffect(() => {
        if (packageData?.forwardAddress && forwardAddresses.length > 0) {
            const matchAddress = forwardAddresses.find(
                (address) => address.id === packageData.forwardAddress.id
            );
            setForwardAddress(matchAddress || null);
        } else if (packageData && forwardAddresses.length === 0) {
            setForwardAddress(null);
        }
    }, [forwardAddresses, packageData]);

    useEffect(() => {
        if (packageData?.deliveryAddress && deliveryAddresses.length > 0) {
            const matchAddress = deliveryAddresses.find(
                (address) => address.id === packageData.deliveryAddress.id
            );
            setDeliveryAddress(matchAddress || null);
        } else if (packageData && deliveryAddresses.length === 0) {
            setDeliveryAddress(null);
        }
    }, [packageData, deliveryAddresses]);


    // Resolves packageData.customer (an id reference) into a full customer object
    // from the fetched list, once, when the dialog first loads a package. This must
    // NOT re-run after that, otherwise every later customer search (including the one
    // triggered by the user picking a different customer) would re-sync against the
    // package's original customer and clear the user's new selection.
    useEffect(() => {
        if (hasInitializedCustomerRef.current) return;
        if (packageData?.customer && customerList?.length > 0) {
            const matchCustomer = customerList.find(
                (c) => c._id === packageData.customer._id
            );
            if (matchCustomer) {
                setCustomer(matchCustomer);
                hasInitializedCustomerRef.current = true;
            }
        } else if (packageData && !packageData.customer) {
            hasInitializedCustomerRef.current = true;
        }
    }, [packageData, customerList]);

    // Fetch route list
    useEffect(() => {
        GetRouteList().then((res) => {
            if (res.data) setRouteList(res.data);
        });
    }, []);

    // Set state value
    useEffect(() => {
        // clearAllState();
        if (packageData) {
            hasInitializedCustomerRef.current = false; // allow the customer-resolution effect to run again for this new package
            // console.log("🚀 ~ useEffect ~ packageData:", packageData)
            const {
                _id,
                tracking,
                customer,
                routeId,
                remark,
                isCOD,
                isDeliveryRequired,
                isForwardRequired,
                isSubCustomer,
                agentId,
                level,
                isSelfOrder,
            } = packageData;
            setSearchCustomer(customer && customer.whatsapp);
            // console.log("first setSearchCustomer")
            setPackageId(_id);
            setTracking(tracking);
            setRemark(remark);
            setRouteId(routeId === "N/A" ? "" : routeId);
            setIsCOD(isCOD);
            setIsDeliveryRequired(isDeliveryRequired);
            setIsForwardRequired(isForwardRequired);
            setIsSubCustomer(isSubCustomer);
            setAgentId(agentId);
            setLevel(level);
            setIsSelfOrder(isSelfOrder);

            originalRef.current = {
                customerId: customer ? customer._id : "",
                routeId: routeId === "N/A" ? "" : routeId,
                remark,
                isCOD,
                isSelfOrder,
                isSubCustomer,
                agentId,
                level,
                isDeliveryRequired,
                isForwardRequired,
                deliveryAddress: packageData.deliveryAddress || null,
                forwardAddress: packageData.forwardAddress || null,
            };
        }
    }, [packageData]);

    // useEffect(() => {
    //   if (searchCustomer && searchCustomer !== "") {
    //     GetCustomersList(searchCustomer).then((res) => {
    //       if (res.status === 200) {
    //         setCustomerList(res.data.customers);
    //       } else {
    //         console.error(res.message);
    //       }
    //     });
    //   }
    // }, [searchCustomer]);

    useEffect(() => {
        if (searchCustomer && searchCustomer !== "") {
            // Clear any existing timeout
            if (searchCustomerTimeout.current) {
                clearTimeout(searchCustomerTimeout.current);
            }

            // Set a new timeout
            searchCustomerTimeout.current = setTimeout(() => {
                GetCustomersList(searchCustomer).then((res) => {
                    if (res && res.success) {
                        setCustomerList(res.data);
                    } else {
                        console.error(res.message);
                    }
                });
            }, 500); // 500ms delay
        } else {
            setCustomerList([]); // Clear customer list if searchCustomer is empty
        }

        // Cleanup function to clear the timeout if the component unmounts or searchCustomer changes
        return () => {
            if (searchCustomerTimeout.current) {
                clearTimeout(searchCustomerTimeout.current);
            }
        };
    }, [searchCustomer]);

    // Fetch agent list for sub-customers
    useEffect(() => {
        if (isSubCustomer) {
            GetAgentList().then((res) => {
                if (res.data) setAgentList(res.data);
            });
        } else {
            setAgentId("");
        }
    }, [isSubCustomer]);

    return (
        <Dialog
            open={open}
            TransitionComponent={Transition}
            maxWidth="sm"
            fullWidth
            fullScreen={isMobile}
            keepMounted
            onClose={onClose}
            aria-describedby="alert-dialog-slide-description"
            PaperProps={{ sx: { borderRadius: isMobile ? 0 : RADIUS_CARD, overflow: "hidden" } }}
            // MUI's FocusTrap only cooperates with other MUI-portalled overlays.
            // SweetAlert2 (the manager-approval reason prompt in handleFormSubmited)
            // renders straight into document.body outside that stack, so without
            // this the trap yanks keyboard focus back into this Dialog on every
            // keystroke and the Swal textarea becomes untypeable.
            disableEnforceFocus
        >
            <form onSubmit={handleFormSubmited}>
                {/* Header — mirrors the mobile-staff scan-edit sheet: close icon, title, tracking subtitle */}
                <Box
                    sx={{
                        display: "flex",
                        alignItems: "center",
                        gap: "12px",
                        p: "16px 20px",
                        borderBottom: `1px solid ${DIVIDER}`,
                        background: BG_WHITE,
                    }}
                >
                    <IconButton
                        aria-label="close"
                        onClick={onClose}
                        sx={{
                            width: 38,
                            height: 38,
                            borderRadius: "12px",
                            background: BG_TINT,
                            "&:hover": { background: BORDER },
                        }}
                    >
                        <CloseOutlined sx={{ fontSize: 20, color: TEXT_BODY }} />
                    </IconButton>
                    <Box sx={{ flex: 1, minWidth: 0 }}>
                        <Box sx={{ fontSize: "17px", fontWeight: 700, color: TEXT_DARK, letterSpacing: "-0.3px" }}>
                            Edit Package for Scan
                        </Box>
                        <Box sx={{ fontSize: "12.5px", color: TEXT_SUBTLE, mt: "2px" }}>
                            {tracking || "—"}
                        </Box>
                    </Box>
                </Box>

                {/* Body */}
                <Box sx={{ p: "20px", background: BG_TINT }}>
                    <Box sx={sectionLabelSx}>Customer &amp; Route</Box>
                    <Box
                        sx={{
                            background: BG_WHITE,
                            border: `1px solid ${BORDER}`,
                            borderRadius: RADIUS_CARD,
                            p: "18px",
                        }}
                    >
                        <Box sx={{ display: "grid", gridTemplateColumns: { xs: "1fr", sm: "1fr 1fr" }, gap: "16px", mb: "16px" }}>
                            {/* Customer Autocomplete */}
                            <Autocomplete
                                onChange={(e, value) => {
                                    setCustomer(value || null);
                                    // When customer is selected, update search to show customer info
                                    if (value) {
                                        setSearchCustomer(`${value.name} - ${value.whatsapp}`);
                                    }
                                }}
                                disablePortal
                                id="combo-box-customer"
                                onInputChange={(event, newInputValue) => {
                                    setSearchCustomer(newInputValue);
                                    setIsNewCustomerSelected(true);
                                }}
                                inputValue={searchCustomer}
                                options={customerList}
                                value={customer}
                                isOptionEqualToValue={(option, value) =>
                                    value && value.id === option.id
                                } // Custom equality check
                                getOptionLabel={(option) =>
                                    `${option.name} - ${option.whatsapp}` || ""
                                }
                                renderOption={(props, option) => (
                                    <Box component="li" {...props} sx={{ display: "flex", alignItems: "center", gap: "10px" }}>
                                        <Avatar
                                            sx={{
                                                width: 28,
                                                height: 28,
                                                fontSize: "11px",
                                                fontWeight: 700,
                                                background: `linear-gradient(135deg,#7c3aed,${PURPLE_DARK})`,
                                            }}
                                        >
                                            {initials(option.name)}
                                        </Avatar>
                                        <Box sx={{ fontSize: "13.5px", fontWeight: 600, color: TEXT_BODY }}>
                                            {option.name}{option.whatsapp ? ` · ${option.whatsapp}` : ""}
                                        </Box>
                                    </Box>
                                )}
                                renderInput={(params) => (
                                    <TextField
                                        {...params}
                                        label="Customer"
                                        sx={fieldSx}
                                        InputProps={{
                                            ...params.InputProps,
                                            startAdornment: customer ? (
                                                <Avatar
                                                    sx={{
                                                        width: 28,
                                                        height: 28,
                                                        fontSize: "11px",
                                                        fontWeight: 700,
                                                        background: `linear-gradient(135deg,#7c3aed,${PURPLE_DARK})`,
                                                        mr: "2px",
                                                    }}
                                                >
                                                    {initials(customer.name)}
                                                </Avatar>
                                            ) : params.InputProps.startAdornment,
                                        }}
                                    />
                                )}
                            />

                            {/* Route Selection */}
                            <TextField
                                select
                                label="Route"
                                id="route"
                                value={(!routeId || routeId === "N/A") ? "" : routeId}
                                onChange={(event) => setRouteId(event.target.value)}
                                required
                                sx={fieldSx}
                            >
                                {routeOptions.map(({ id, name }) => (
                                    <MenuItem key={id} value={id}>
                                        {name ? `${id} (${name})` : id}
                                    </MenuItem>
                                ))}
                            </TextField>
                        </Box>

                        {/* COD / Self-order / Sub-customer + Agent */}
                        <Box
                            sx={{
                                display: "grid",
                                gridTemplateColumns: { xs: "1fr", sm: isAgentUser ? "1fr 1fr" : "1fr 1fr 1fr" },
                                gap: "12px",
                                mb: "16px",
                            }}
                        >
                            <ToggleCard
                                label="COD"
                                checked={isCOD}
                                disabled={!customer}
                                onChange={() => setIsCOD(!isCOD)}
                            />

                            <ToggleCard
                                label="Self-order"
                                checked={isSelfOrder}
                                disabled={!customer}
                                onChange={() => setIsSelfOrder(!isSelfOrder)}
                            />

                            {!isAgentUser && (
                                <ToggleCard
                                    label="Sub-customer"
                                    checked={isSubCustomer}
                                    disabled={!customer}
                                    onChange={() => setIsSubCustomer(!isSubCustomer)}
                                >
                                    {isSubCustomer && (
                                        <TextField
                                            select
                                            fullWidth
                                            size="small"
                                            label="Agent"
                                            value={agentId || ""}
                                            required
                                            sx={accentFieldSx}
                                            onChange={(event) => setAgentId(event.target.value)}
                                        >
                                            {agentList &&
                                                agentList.map(({ id, name }) => (
                                                    <MenuItem key={id} value={id}>
                                                        {id} - {name}
                                                    </MenuItem>
                                                ))}
                                        </TextField>
                                    )}
                                </ToggleCard>
                            )}
                        </Box>

                        {/* Remark */}
                        <TextField
                            fullWidth
                            multiline
                            maxRows={5}
                            label="Remark"
                            value={remark}
                            id="remark"
                            sx={fieldSx}
                            onChange={(e) => setRemark(e.target.value)}
                        />
                    </Box>
                    {/* Scan-edit is intentionally minimal: Customer, Route, COD / Self-order /
                        Sub-customer + Agent, and Remark are shown. Station is kept from the
                        parcel (packageData.station) and Home delivery / Forward are inherited
                        from the selected customer's defaults (set in the customer-selection
                        effect above) — hidden here. */}
                </Box>

                {/* Footer: Actions */}
                <Box
                    sx={{
                        display: "flex",
                        justifyContent: "flex-end",
                        gap: "12px",
                        p: "16px 20px",
                        borderTop: `1px solid ${DIVIDER}`,
                        background: BG_WHITE,
                    }}
                >
                    <Button variant="outlined" color="secondary" onClick={onClose}>
                        Cancel
                    </Button>
                    <Button variant="contained" color="secondary" type="submit" size="large">
                        {canEditDirectly || isScanAssignNow() ? "Save" : t("packageChangeRequest.submitButton")}
                    </Button>
                </Box>
            </form>
        </Dialog>
    );
};

export default PackageEditDialogForScan;
